import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import hotelService from '../../services/hotelService';
import pointService from '../../services/pointService';
import { useAuth } from '../../contexts/AuthContext';
import { mapPointToPlace, mapHotelToPoint, POINT_IMAGE_PLACEHOLDER } from './constants';
import SearchHero from './components/SearchHero';
import SectionTitle from './components/SectionTitle';
import TrendingCard from './components/TrendingCard';
import SeasonCard from './components/SeasonCard';
import HighlightCard from './components/HighlightCard';
import useImageLightbox from '../../hooks/useImageLightbox';
import { normalizeUrlList } from '../../utils/imageLightbox';

const PointsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [pointCategories, setPointCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [points, setPoints] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [pointsError, setPointsError] = useState(null);
  const [activeCategorySlug, setActiveCategorySlug] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFetched, setLocationFetched] = useState(false);
  const userLocationRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);
  const scrollRef = useRef(null);
  const [hotels, setHotels] = useState([]);
  const [hotelsActiveDot, setHotelsActiveDot] = useState(0);
  const hotelsScrollRef = useRef(null);

  const [regionsLeft, setRegionsLeft] = useState([]);
  const [regionsRight, setRegionsRight] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [culturalHighlights, setCulturalHighlights] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [travelTips, setTravelTips] = useState([]);
  const { openLightbox, Lightbox } = useImageLightbox();
  const visitorGallerySlides = useMemo(
    () => normalizeUrlList(galleryImages.map((img) => img.image_url), 'Visitor gallery'),
    [galleryImages],
  );
  const [seasonDetail, setSeasonDetail] = useState(null);

  useEffect(() => {
    if (locationFetched) return;
    hotelService
      .getUserLocation()
      .then((loc) => {
        userLocationRef.current = loc;
        setLocationFetched(true);
      })
      .catch(() => setLocationFetched(true));
  }, [locationFetched]);

  useEffect(() => {
    hotelService
      .getPointCategories()
      .then((raw) => {
        const list = Array.isArray(raw) ? raw : (raw?.results ?? []);
        setPointCategories(list);
        if (list.length > 0) {
          setActiveCategorySlug((prev) => prev || list[0].slug || '');
        }
      })
      .catch(() => {
        setPointCategories([]);
      })
      .finally(() => setCategoriesLoading(false));
  }, []);

  const loadPoints = useCallback(async () => {
    try {
      setPointsLoading(true);
      setPointsError(null);
      const params = { page_size: 12, ordering: '-is_featured,-average_rating' };
      if (activeCategorySlug) {
        params.point_category = activeCategorySlug;
      }
      const response = await pointService.getPoints(params);
      const list = response?.results ?? response?.data?.results ?? (Array.isArray(response) ? response : []);
      setPoints(list);
    } catch (err) {
      console.error('Failed to load points:', err);
      setPointsError('Failed to load points right now.');
      setPoints([]);
    } finally {
      setPointsLoading(false);
    }
  }, [activeCategorySlug]);

  useEffect(() => {
    if (locationFetched) loadPoints();
  }, [locationFetched, loadPoints]);

  const loadHotels = useCallback(async () => {
    try {
      const response = await hotelService.getHotels({
        page_size: 6,
        ordering: '-is_featured',
      });
      const list = response?.results ?? response?.data?.results ?? (Array.isArray(response) ? response : []);
      setHotels(list);
    } catch {
      setHotels([]);
    }
  }, []);

  useEffect(() => {
    if (locationFetched) loadHotels();
  }, [locationFetched, loadHotels]);

  useEffect(() => {
    Promise.all([
      pointService.getRegions(),
      pointService.getSeasons(),
      pointService.getCulturalHighlights(),
      pointService.getGalleryImages(),
      pointService.getTravelTips(),
      pointService.getSeasonDetail(),
    ])
      .then(([regions, seasonsList, highlights, gallery, tips, detail]) => {
        const list = Array.isArray(regions) ? regions : [];
        setRegionsLeft(list.filter((r) => r.column === 'left'));
        setRegionsRight(list.filter((r) => r.column === 'right'));
        setSeasons(Array.isArray(seasonsList) ? seasonsList : []);
        setCulturalHighlights(Array.isArray(highlights) ? highlights : []);
        setGalleryImages(Array.isArray(gallery) ? gallery : []);
        setTravelTips(Array.isArray(tips) ? tips : []);
        setSeasonDetail(detail && typeof detail === 'object' ? detail : null);
      })
      .catch(() => {
        setRegionsLeft([]);
        setRegionsRight([]);
        setSeasons([]);
        setCulturalHighlights([]);
        setGalleryImages([]);
        setTravelTips([]);
        setSeasonDetail(null);
      });
  }, []);

  const places = useMemo(() => points.map(mapPointToPlace), [points]);
  const trendingPlaces = places.slice(0, 6);
  const hotelPlaces = useMemo(() => hotels.map(mapHotelToPoint), [hotels]);
  const recentlyAddedPlace = places.length > 0 ? (places.find((p) => p.seasonTag) || places[places.length - 1]) : null;

  const onViewDetails = useCallback(
    (place) => {
      if (!place) return;
      const target = place.slug || place.id;
      if (target) navigate(`/points/${target}`);
    },
    [navigate],
  );

  const onViewHotelDetails = useCallback(
    (place) => {
      if (!place) return;
      const target = place.slug || place.id;
      if (target) navigate(`/hotels/${target}`);
    },
    [navigate],
  );

  const handleHotelsScroll = useCallback(() => {
    const el = hotelsScrollRef.current;
    if (!el) return;
    const cardWidth = 272;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setHotelsActiveDot(Math.min(idx, Math.max(0, Math.ceil(hotelPlaces.length / 2) - 1)));
  }, [hotelPlaces.length]);

  const onSubmitPoint = useCallback(() => {
    if (isAuthenticated) navigate('/points/create');
    else navigate('/login', { state: { from: '/points/create' } });
  }, [isAuthenticated, navigate]);

  const onSubmitHotel = useCallback(() => {
    if (isAuthenticated) navigate('/hotels/create');
    else navigate('/login', { state: { from: '/hotels/create' } });
  }, [isAuthenticated, navigate]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 272;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setActiveDot(Math.min(idx, 2));
  }, []);

  return (
    <PageLayout title="Points">
      <div className="min-h-screen bg-[#f5f5f0]">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
          {/* ── Page title ── */}
          <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-5">
            Discover <span className="font-bold">Pakistan</span> – Places, Resorts &amp;
            Attractions
          </h2>

          {/* ── Search hero (unchanged) ── */}
          <SearchHero searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

          {pointsLoading && (
            <div className="py-12 flex justify-center">
              <LoadingSpinner />
            </div>
          )}

          {!pointsLoading && pointsError != null && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              {pointsError}
            </div>
          )}

          {!pointsLoading && (
            <>
              {/* ═══════════════════════════════════
                  1. CATEGORY CHIPS (from API or fallback)
                  ═══════════════════════════════════ */}
              <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-6">
                <div className="flex gap-2.5 min-w-max pb-1">
                  {(categoriesLoading ? [] : pointCategories).map((cat) => {
                    const isActive = activeCategorySlug === cat.slug;
                    return (
                      <button
                        key={cat.slug}
                        type="button"
                        onClick={() => setActiveCategorySlug(cat.slug)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                          isActive
                            ? 'bg-emerald-700 text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-emerald-300'
                        }`}
                      >
                        {cat.icon != null && cat.icon !== '' && <span aria-hidden>{cat.icon}</span>}
                        {cat.name}
                        {typeof cat.listing_count === 'number' && cat.listing_count > 0 && (
                          <span className={isActive ? 'opacity-90' : 'text-gray-400'}>
                            ({cat.listing_count})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ═══════════════════════════════════
                  2. TRENDING NOW
                  ═══════════════════════════════════ */}
              <SectionTitle icon="🔥" title="Trending Now" />
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-2"
              >
                <div className="flex gap-4 min-w-max pb-2 snap-x snap-mandatory">
                  {trendingPlaces.map((place) => (
                    <TrendingCard
                      key={place.id}
                      place={place}
                      onViewDetails={onViewDetails}
                      className="w-[260px] sm:w-[280px] shrink-0 snap-start"
                    />
                  ))}
                </div>
              </div>

              {/* Pagination dots */}
              <div className="flex justify-center gap-1.5 mb-8">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      activeDot === i ? 'bg-emerald-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* ═══════════════════════════════════
                  2b. HOTELS (same UI as Trending)
                  ═══════════════════════════════════ */}
              <SectionTitle icon="🏨" title="Hotels" />
              <div
                ref={hotelsScrollRef}
                onScroll={handleHotelsScroll}
                className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-2"
              >
                <div className="flex gap-4 min-w-max pb-2 snap-x snap-mandatory">
                  {hotelPlaces.map((place) => (
                    <TrendingCard
                      key={`hotel-${place.id}`}
                      place={place}
                      onViewDetails={onViewHotelDetails}
                      className="w-[260px] sm:w-[280px] shrink-0 snap-start"
                    />
                  ))}
                </div>
              </div>
              {hotelPlaces.length > 0 && (
                <div className="flex justify-center gap-1.5 mb-8">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        hotelsActiveDot === i ? 'bg-emerald-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* ═══════════════════════════════════
                  3. EXPLORE BY REGION (2 columns)
                  ═══════════════════════════════════ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                  <SectionTitle icon="🌍" title="Explore by Region" />
                  <div className="flex flex-wrap gap-2">
                    {regionsLeft.map((r) => (
                      <button
                        key={r.slug || r.id}
                        type="button"
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                  <SectionTitle icon="🌍" title="Explore by Region" />
                  <div className="flex flex-wrap gap-2">
                    {regionsRight.map((r) => (
                      <button
                        key={r.slug || r.id}
                        type="button"
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════
                  4. RECENTLY ADDED + BEST TIME TO VISIT
                  ═══════════════════════════════════ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Recently Added */}
                <div>
                  <SectionTitle icon="📦" title="Recently Added" badge="NEW" />
                  {recentlyAddedPlace ? (
                    <TrendingCard
                      place={recentlyAddedPlace}
                      onViewDetails={onViewDetails}
                      className="w-full"
                    />
                  ) : (
                    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm flex items-center justify-center min-h-[120px] text-sm text-gray-500">
                      No recently added places
                    </div>
                  )}
                </div>

                {/* Best Time to Visit */}
                <div>
                  <SectionTitle icon="☀️" title="Best Time to Visit" />
                  <div className="overflow-x-auto scrollbar-hide -mr-4 pr-4">
                    <div className="flex gap-3 min-w-max">
                      {seasons.map((s) => (
                        <SeasonCard
                          key={s.id}
                          season={{
                            id: s.id,
                            name: s.name,
                            months: s.months,
                            tags: Array.isArray(s.tags) ? s.tags : [],
                            imageUrl: s.image_url || POINT_IMAGE_PLACEHOLDER,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════
                  5. CULTURAL & HISTORICAL + BEST TIME DETAIL
                  ═══════════════════════════════════ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Cultural & Historical */}
                <div>
                  <SectionTitle icon="🏛️" title="Cultural & Historical Highlights" />
                  <div className="space-y-3">
                    {culturalHighlights.map((item) => (
                      <HighlightCard
                        key={item.id}
                        item={{
                          id: item.id,
                          title: item.title,
                          locationText: item.location_text,
                          infoText: item.info_text,
                          imageUrl: item.image_url || POINT_IMAGE_PLACEHOLDER,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Best Time detail card */}
                <div>
                  <SectionTitle icon="☀️" title="Best Time to Visit" />
                  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <h4 className="font-semibold text-gray-900 text-base">
                        {seasonDetail ? seasonDetail.name : '—'}
                      </h4>
                      <div className="flex items-center gap-1.5">
                        <span className="text-amber-400 text-sm">☀️</span>
                        <span className="text-amber-400 text-sm">🌡️</span>
                      </div>
                    </div>
                    <p className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
                      <span className="w-2 h-2 rounded-full border border-gray-300" />
                      {seasonDetail ? seasonDetail.months : '—'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {seasonDetail && Array.isArray(seasonDetail.tags) && seasonDetail.tags.length > 0
                        ? seasonDetail.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              {tag}
                            </span>
                          ))
                        : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                            </svg>
                            —
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════
                  6. VISITOR GALLERY + TRAVEL TIPS
                  ═══════════════════════════════════ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Gallery */}
                <div>
                  <SectionTitle icon="📸" title="Visitor Gallery" />
                  <div className="grid grid-cols-2 gap-2">
                    {galleryImages.map((img, idx) => (
                      <button
                        key={img.id || idx}
                        type="button"
                        onClick={() => openLightbox(visitorGallerySlides, idx)}
                        className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                        aria-label={`View gallery image ${idx + 1}`}
                      >
                        <img
                          src={img.image_url}
                          alt=""
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Travel Tips */}
                <div>
                  <SectionTitle icon="🧭" title="Travel Tips" />
                  <div className="space-y-3">
                    {travelTips.map((tip) => (
                      <div
                        key={tip.id}
                        className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-2.5">
                          <span className="text-lg leading-none mt-0.5" aria-hidden>
                            {tip.icon}
                          </span>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm">
                              {tip.title}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {tip.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════
                  7. SUBMIT CTA
                  ═══════════════════════════════════ */}
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 sm:p-8 text-center">
                <h3 className="text-lg font-bold text-gray-900">
                  Know a place worth sharing?
                </h3>
                <p className="text-sm text-gray-600 mt-1.5 max-w-md mx-auto">
                  Submit a tourist spot or list accommodation to help others discover Pakistan.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={onSubmitPoint}
                    className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 text-white px-6 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors shadow-sm"
                  >
                    Submit a Point
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={onSubmitHotel}
                    className="inline-flex items-center gap-2 rounded-xl border border-emerald-700 text-emerald-800 bg-white px-6 py-2.5 text-sm font-medium hover:bg-emerald-50 transition-colors shadow-sm"
                  >
                    List a Hotel
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    {Lightbox}
    </PageLayout>
  );
};

export default PointsPage;
